import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ScrollText, UserCog } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const ROLE_LABELS = { super_admin: "Super admin", admin: "Admin", staff: "Staff" };

export const AdminUsersSection = () => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success("Compte créé");
      setForm(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer le compte ${u.email} ?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Compte supprimé");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="mt-14" data-testid="admin-users-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif-elegant text-xl font-semibold flex items-center gap-2"><UserCog size={18} className="text-[#D4AF37]" /> Utilisateurs admin & staff</h2>
        <button data-testid="admin-user-add-btn" onClick={() => setForm({ email: "", password: "", first_name: "", last_name: "", role: "staff" })} className="btn-outline-gold !py-1.5 !px-4 text-xs"><Plus size={13} /> Ajouter</button>
      </div>
      {form && (
        <form onSubmit={save} className="ic-card !transform-none p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-user-form">
          <input data-testid="admin-user-firstname" required className="ic-input" placeholder="Prénom *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          <input data-testid="admin-user-lastname" required className="ic-input" placeholder="Nom *" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          <input data-testid="admin-user-email" required type="email" className="ic-input" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="admin-user-password" required type="password" minLength={6} className="ic-input" placeholder="Mot de passe (min 6) *" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <select data-testid="admin-user-role" className="ic-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="staff">Staff — gestion quotidienne</option>
            <option value="admin">Admin — accès complet</option>
          </select>
          <div className="flex gap-2 items-center">
            <button type="submit" data-testid="admin-user-save" className="btn-gold !py-1.5 !px-4 text-xs">Créer le compte</button>
            <button type="button" onClick={() => setForm(null)} className="btn-outline-gold !py-1.5 !px-4 text-xs">Annuler</button>
          </div>
        </form>
      )}
      <div className="ic-card !transform-none overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-left">
            <th className="p-4 gold-label">Nom</th><th className="p-4 gold-label">Email</th><th className="p-4 gold-label">Rôle</th><th className="p-4 gold-label">Actions</th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-white/5" data-testid={`admin-user-row-${u.id}`}>
                <td className="p-4 font-medium">{u.first_name} {u.last_name}</td>
                <td className="p-4 text-gray-400">{u.email}</td>
                <td className="p-4">
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${u.role === "staff" ? "border-white/20 text-gray-300" : "border-[#D4AF37]/40 text-[#D4AF37]"}`}>{ROLE_LABELS[u.role] || u.role}</span>
                </td>
                <td className="p-4">
                  {u.role !== "super_admin" && (
                    <button data-testid={`admin-user-delete-${u.id}`} onClick={() => remove(u)} className="p-1.5 text-gray-400 hover:text-[#ff6b81]"><Trash2 size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AdminAuditSection = () => {
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    api.get("/admin/audit").then((r) => setLogs(r.data)).catch(() => {});
  }, []);

  const ACTION_LABELS = {
    ritas_adjust: "Ajustement RITAS",
    customer_delete_gdpr: "Suppression RGPD",
    admin_user_create: "Création compte",
    admin_user_delete: "Suppression compte",
  };

  return (
    <div className="mt-14" data-testid="admin-audit-section">
      <h2 className="font-serif-elegant text-xl font-semibold mb-4 flex items-center gap-2"><ScrollText size={18} className="text-[#D4AF37]" /> Registre d'activité</h2>
      <div className="ic-card !transform-none overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#0a0a0a]"><tr className="border-b border-white/10 text-left">
            <th className="p-3 gold-label">Date</th><th className="p-3 gold-label">Admin</th><th className="p-3 gold-label">Action</th><th className="p-3 gold-label">Détails</th>
          </tr></thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} className="border-b border-white/5" data-testid={`audit-row-${l.id}`}>
                <td className="p-3 text-gray-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</td>
                <td className="p-3 text-gray-300">{l.admin}</td>
                <td className="p-3 text-[#D4AF37] text-xs">{ACTION_LABELS[l.action] || l.action}</td>
                <td className="p-3 text-gray-400">{l.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {logs.length === 0 && <p className="p-5 text-gray-500 text-sm">Aucune activité enregistrée.</p>}
      </div>
    </div>
  );
};
